"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import { CreditCard, Banknote, Trash2, AlertTriangle } from "lucide-react"
import { Guard } from "@/components/access/Guard"
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";
// NOTE: appointment state comes from context just like other sections
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useComputedPayment } from "@/hooks/modal/useComputedPayment";
import { lastHourOfferPct } from "@/lib/appointment/utils";
import { useAppointmentModalPaymentInfo } from "@/hooks/modal/useAppointmentModalPaymentInfo";
// Centralized cost calculation engine
import {
  round2,
  getServiceUnitPrice,
  getServiceLineTotal,
  calculateServiceCostFields,
  applyManualPriceChange,
  applyDiscountChange,
  applyQuantityChange,
} from "@/lib/appointment/costEngine";
import { formatCurrency } from "@/lib/utils";
import { goToDashboard } from "@/features/sso/goToDashboard";
import { PackagePaySelect, usePackageUses } from "./package-pay-select";
import { SellPackageButton } from "./sell-package-dialog";
import { coveredServiceIds } from "@/lib/appointment/abonements";


enum PaymentMode {
  QUICK = "quick",
  SPLIT = "split",
}

type SplitAmounts = Record<number, number>;

export function PaymentSection() {
  const { t } = useTranslation()
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(PaymentMode.QUICK)
  const [splitAmounts, setSplitAmounts] = useState<SplitAmounts>({})
  const [editingLineTotal, setEditingLineTotal] = useState<{ [key: number]: string }>({})
  const [editingDiscount, setEditingDiscount] = useState<{ [key: number]: string }>({})
  const [splitEditState, setSplitEditState] = useState<Record<number, string | undefined>>({})
  const { appointment, updateAppointment } = useAppointmentModal();
  const payment = useComputedPayment();
  const packageCovered = coveredServiceIds(usePackageUses().uses);
  const { data: backendAppointment } = useAppointmentModalPaymentInfo();
  const paymentMethods = appointment?.paymentSection?.paymentMethods ?? [];
  const totalAmount = payment?.totalAmount ?? 0;
  const paymentInitRef = useRef(false);

  // Calculate paid_full status directly from appointment data
  const isPaidFull = useMemo(() => {
    // Derive paid state from persisted backend data so the banner only appears on load
    // and recognize "paid" statuses represented by either 1 or 2 (mirrors backend utils).
    const backendPaidFull = backendAppointment?.paid_full ?? backendAppointment?.paymentSection?.paidFull;
    return backendPaidFull === 1 || backendPaidFull === 2 || backendAppointment?.paymentSection?.isPaid;
  }, [backendAppointment]);

  // --- helpers for amount stepper (+ sanitize quantity) ---
  const toInt = (v: any, min = 1) => {
    const n = parseInt(String(v ?? "").replace(/[^\d]/g, ""), 10);
    if (Number.isNaN(n)) return min;
    return Math.max(min, n);
  };
  const keepQuantityIfPresent = (s: any, qty: number) =>
    Object.prototype.hasOwnProperty.call(s, "quantity") ? { quantity: qty } : {};


  // --- Calculation helpers now imported from costEngine ---
  // Using: calculateServiceCostFields, applyManualPriceChange, applyDiscountChange
  // from @/lib/appointment/costEngine


  const updateServiceAt = (idx: number, patch: any) => {
    if (!appointment?.services) return;

    const oldService = appointment.services[idx];

    // Detect if this is a quantity-only change (no discount or manual_cost in patch)
    const isQuantityOnlyChange =
      (patch.amount !== undefined || patch.quantity !== undefined) &&
      patch.discount === undefined &&
      patch.manual_cost === undefined &&
      patch.cost_to_pay === undefined;

    let costFields;

    if (isQuantityOnlyChange) {
      // Use centralized applyQuantityChange which properly handles manual_cost
      // It will clear manual_cost if it equals base cost (not a real override)
      const newQuantity = patch.amount ?? patch.quantity ?? oldService.amount ?? 1;
      costFields = applyQuantityChange(oldService as any, newQuantity);
    } else {
      // For discount/price changes, use calculateServiceCostFields
      const mergedService = { ...oldService, ...patch };
      costFields = calculateServiceCostFields({
        cost: Number(oldService.cost) || getServiceUnitPrice(oldService),
        amount: mergedService.amount ?? mergedService.quantity ?? 1,
        discount: mergedService.discount ?? 0,
        manualCost: mergedService.manual_cost,
        firstCost: oldService.first_cost ?? undefined,
        isCreation: false,
      });
    }

    // Construct the fully updated service object
    const nextService = {
      ...oldService,
      ...patch,
      ...costFields,
      price: costFields.cost_to_pay,
      // Preserve quantity alias if it existed
      ...(Object.prototype.hasOwnProperty.call(oldService, 'quantity')
        ? { quantity: costFields.amount }
        : {}),
    };

    // Compose new services array
    const nextServices = appointment.services.map((s: any, i: number) => (i === idx ? nextService : s));

    // Calculate new Grand Total for payment sync
    const newTotalAmount = nextServices.reduce((sum: number, s: any) => sum + (Number(s.cost_to_pay) || 0), 0);
    const roundedTotal = round2(newTotalAmount);

    // Update payment methods if in Quick mode
    const currentMethods = appointment.paymentSection?.paymentMethods ?? [];
    let nextMethods = currentMethods;

    if (paymentMode === PaymentMode.QUICK && selectedAccountId != null) {
      nextMethods = currentMethods.map((m: any) => {
        if (m?.account?.id === selectedAccountId) {
          return {
            ...m,
            // Update the amount fields to reflect the new total
            applicableAmount: roundedTotal,
            account: {
              ...m.account,
              paid_amount: roundedTotal,
            },
          };
        }
        // Reset others
        return {
          ...m,
          applicableAmount: 0,
          account: {
            ...m.account,
            paid_amount: 0,
          },
        };
      });
    }

    // Persist to context
    // The change in 'services' array reference will force 'useComputedPayment' to recalculate.
    updateAppointment({
      services: nextServices,
      paymentSection: {
        ...(appointment.paymentSection ?? {}),
        paymentMethods: nextMethods,
        paymentMode,
      },
    });
  };


  const handleEditLineTotal = (idx: number, raw: string) => {
    const rawCleaned = String(raw || "").replace(",", ".");
    const targetLineTotal = Math.max(0, Number(rawCleaned) || 0);
    const s = appointment?.services?.[idx];
    if (!s) return;

    // Use centralized applyManualPriceChange from costEngine
    const costPatch = applyManualPriceChange(s as any, targetLineTotal);

    updateServiceAt(idx, costPatch);
    setEditingDiscount((prev) => ({ ...prev, [idx]: String(costPatch.discount ?? 0) }));
  };

  const handleEditDiscount = (idx: number, raw: string) => {
    const rawCleaned = String(raw || "").replace(",", ".");
    const discountVal = Math.max(0, Math.min(100, Number(rawCleaned) || 0));
    const s = appointment?.services?.[idx];
    if (!s) return;

    // Use centralized applyDiscountChange from costEngine
    const costPatch = applyDiscountChange(s as any, discountVal);

    updateServiceAt(idx, costPatch);
  };

  const handleRemoveService = (id: number | string) => {
    if (!Array.isArray(appointment?.services)) return;
    // An appointment must keep at least one service — saving an empty list
    // strips every yrecordservice row and orphans the record. The backend
    // rejects it too; this stops the admin reaching a save that can only fail.
    // Removing the appointment itself is Delete, not an empty service list.
    if (appointment.services.length <= 1) return;
    const updatedServices = appointment.services.filter((service: any) => service.id !== id);
    updateAppointment({ services: updatedServices });
  };
  useEffect(() => {
    if (
      backendAppointment &&
      backendAppointment.paymentSection
    ) {
      paymentInitRef.current = false;
      setSelectedAccountId(null);
      setSplitAmounts({});
      setSplitEditState({});
      setPaymentMode(PaymentMode.QUICK);
      updateAppointment({ paymentSection: backendAppointment.paymentSection });
    }
  }, [backendAppointment, updateAppointment]);

  useEffect(() => {
    paymentInitRef.current = false;
    setSelectedAccountId(null);
    setSplitAmounts({});
    setSplitEditState({});
    setPaymentMode(PaymentMode.QUICK);
  }, [appointment?.id]);

  useEffect(() => {
    if (!appointment || !paymentMethods.length || paymentInitRef.current) return;

    const paidMethods = paymentMethods.filter((m: any) => Number(m?.account?.paid_amount) > 0);
    const initialSplit: SplitAmounts = {};
    paymentMethods.forEach((m: any) => {
      const id = m?.account?.id;
      if (id != null) {
        const paid = Number(m?.account?.paid_amount);
        initialSplit[id] = Number.isFinite(paid) ? paid : 0;
      }
    });

    setSplitAmounts(initialSplit);
    // Do not preselect any payment method; wait for explicit user choice
    setSelectedAccountId(null);
    setPaymentMode(paidMethods.length > 1 ? PaymentMode.SPLIT : PaymentMode.QUICK);
    paymentInitRef.current = true;
  }, [appointment, paymentMethods]);

  useEffect(() => {
    if (!paymentInitRef.current) return;
    if (paymentMode !== PaymentMode.SPLIT) return;
    const hasDiff = paymentMethods.some((m: any) => {
      const id = m?.account?.id;
      const current = Number(m?.account?.paid_amount) || 0;
      const next = id != null ? Number(splitAmounts[id]) || 0 : 0;
      return Math.abs(current - next) >= 0.005;
    });
    if (!hasDiff) return;
    applySplitAmounts(splitAmounts);
  }, [paymentMode, splitAmounts, paymentMethods]);

  // Defensive destructuring
  const client = appointment?.client ?? null
  const services = Array.isArray(appointment?.services) ? appointment.services : []
  const paymentSection = appointment?.paymentSection ?? {}
  const splitTotal = useMemo(
    () =>
      Object.values(splitAmounts).reduce(
        (sum, raw) => sum + (Number.isFinite(Number(raw)) ? Number(raw) : 0),
        0
      ),
    [splitAmounts]
  );
  const isSplitMode = paymentMode === PaymentMode.SPLIT;
  const isSplitValid = !isSplitMode || Math.abs(splitTotal - totalAmount) < 0.01;

  const areMethodsEqual = (left: any[], right: any[]) => {
    if (left.length !== right.length) return false;
    return left.every((m, idx) => {
      const n = right[idx];
      const mId = m?.account?.id;
      const nId = n?.account?.id;
      if (mId !== nId) return false;
      const mPaid = Number(m?.account?.paid_amount) || 0;
      const nPaid = Number(n?.account?.paid_amount) || 0;
      return Math.abs(mPaid - nPaid) < 0.005;
    });
  };
  const applyPaymentSection = (methods: any[], mode: PaymentMode = paymentMode) => {
    const paidTotal = methods.reduce(
      (sum, m) => sum + (Number(m?.account?.paid_amount) || 0),
      0
    );
    const matchesTotal = Math.abs(paidTotal - totalAmount) < 0.01;
    // In split mode we keep the state as "partial" to avoid hiding the UI mid-edit,
    // even if the split totals happen to match the full amount. In quick mode we
    // also avoid auto-marking as fully paid; the final save action should decide.
    const paidFull =
      paidTotal > 0
        ? mode === PaymentMode.QUICK
          ? 2
          : matchesTotal
            ? 1
            : 2
        : 0;

    if (
      areMethodsEqual(paymentMethods, methods) &&
      paymentSection?.isPaid === (paidTotal > 0) &&
      paymentSection?.paidFull === paidFull &&
      paymentSection?.paymentMode === mode &&
      appointment?.paid_full === paidFull &&
      appointment?.is_paid === (paidTotal > 0)
    ) {
      return;
    }

    updateAppointment({
      paymentSection: {
        ...paymentSection,
        paymentMethods: methods,
        isPaid: paidTotal > 0,
        paidFull,
        paymentMode: mode,
      },
      paid_full: paidTotal > 0 ? paidFull : undefined,
      is_paid: paidTotal > 0,
    });
  };

  const applyQuickSelection = (accountId: number | null) => {
    if (!accountId || !paymentMethods.length) return;
    setSelectedAccountId(accountId);
    const updatedMethods = paymentMethods.map((m: any) =>
      m?.account
        ? {
          ...m,
          account: {
            ...m.account,
            paid_amount: m.account.id === accountId ? totalAmount : 0,
          },
        }
        : m
    );
    applyPaymentSection(updatedMethods, PaymentMode.QUICK);
  };

  const applySplitAmounts = (amounts: SplitAmounts = splitAmounts) => {
    if (!paymentMethods.length) return;
    const updatedMethods = paymentMethods.map((m: any) =>
      m?.account
        ? {
          ...m,
          account: {
            ...m.account,
            paid_amount: Number(amounts[m.account.id]) || 0,
          },
        }
        : m
    );
    applyPaymentSection(updatedMethods, PaymentMode.SPLIT);
  };

  const handleSplitAmountChange = (accountId: number, value: string) => {
    setSplitEditState((prev) => ({ ...prev, [accountId]: value }));
  };

  const applySplitAmountUpdate = (accountId: number, rawValue: string) => {
    const rawCleaned = String(rawValue ?? "").replace(",", ".");
    const numeric = Number(rawCleaned);
    const nextValue = Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;

    setSplitAmounts((prev) => ({ ...prev, [accountId]: nextValue }));
    setSplitEditState((prev) => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  };

  const handleSplitInputFocus = (accountId: number) => {
    setSplitEditState((prev) => {
      if (splitAmounts[accountId] === 0 || splitAmounts[accountId] === undefined) {
        return { ...prev, [accountId]: "" };
      }
      const currentAmount = splitAmounts[accountId] ?? 0;
      return { ...prev, [accountId]: currentAmount.toString() };
    });
  };

  const handleSplitInputBlur = (accountId: number, rawValue: string) => {
    applySplitAmountUpdate(accountId, rawValue);
  };

  if (!appointment) {
    return (
      <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center text-gray-500">
        {t("appointment.payment.no_data", "No payment data.")}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Client info */}
      <div className="mb-6">
        <div className="text-xs text-gray-500 mb-1">{t("appointment.payment.client", "Client")}</div>
        <div className="text-sm">
          {client?.name || "—"}
          <Guard
            check={(p) => p.timetable.phonesVisible}
            mode="render"
            fallback={null}
          >
            {client?.phone ? (
              <span className="ml-2 text-xs text-gray-500">{client.phone}</span>
            ) : null}
          </Guard>
        </div>
      </div>

      {/* Services table */}
      <div className="mb-6">
        <div className="grid grid-cols-12 gap-2 mb-2 text-xs text-gray-500 border-b pb-2">
          <div className="col-span-2">{t("appointment.payment.quantity", "Quantity")}</div>
          <div className="col-span-3">{t("appointment.payment.position", "Position")}</div>
          <div className="col-span-2">{t("appointment.payment.discount_percent", "Discount, %")}</div>
          <div className="col-span-3 text-center">{t("appointment.payment.amount_byn", "Amount")}</div>
          <div className="col-span-1 text-right">{t("appointment.payment.paid_byn", "Paid")}</div>
          <div className="col-span-1"></div>
        </div>

        {services.map((service, idx) => (
          <div key={service.id} className="relative grid grid-cols-12 gap-2 py-4 border-b items-center">
            {lastHourOfferPct(service) !== null && (
              <div className="absolute -top-2.5 right-10 z-10 flex items-center gap-1.5">
                <span
                  className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm"
                  title={t("appointment.last_hour_hint", "Booked via the last-hour offer — the badge shows the original offer discount")}
                >
                  ⚡ {t("appointment.last_hour_badge", "Last-minute")}{(lastHourOfferPct(service) ?? 0) > 0 ? ` −${lastHourOfferPct(service)}%` : ""}
                </span>
                {Number(service.discount) > 0 && !isPaidFull && (
                  <button
                    type="button"
                    className="rounded-full border bg-white px-2 py-0.5 text-[10px] text-muted-foreground shadow-sm hover:text-foreground"
                    onClick={() => handleEditDiscount(idx, "0")}
                    title={t("appointment.payment.charge_regular_hint", "Remove the last-minute discount and charge the regular price")}
                  >
                    {t("appointment.payment.charge_regular", "charge regular price")}
                  </button>
                )}
              </div>
            )}
            {/* Amount (integer-only with stepper) */}
            <div className="col-span-2">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => {
                    const next = Math.max(1, Number(service.amount || 1) - 1);
                    updateServiceAt(idx, { amount: next, ...keepQuantityIfPresent(service, next) });
                  }}
                  aria-label={t("common.decrease", "Decrease")}
                  disabled={isPaidFull}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="w-16 h-8 text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  value={String(service.amount ?? 1)}
                  disabled={isPaidFull}
                  onChange={(e) => {
                    const next = toInt(e.target.value, 1);
                    updateServiceAt(idx, { amount: next, ...keepQuantityIfPresent(service, next) });
                  }}
                  onBlur={(e) => {
                    const next = toInt(e.target.value, 1);
                    if (String(next) !== e.target.value) {
                      // normalize UI text if user typed weird stuff like "02." or "2,0"
                      e.target.value = String(next);
                    }
                    updateServiceAt(idx, { amount: next, ...keepQuantityIfPresent(service, next) });
                  }}
                  // prevent accidental mouse-wheel changes
                  onWheel={(e) => (e.currentTarget as HTMLElement).blur()}
                  aria-label={t("appointment.payment.quantity", "Quantity")}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => {
                    const next = Math.max(1, Number(service.amount || 1) + 1);
                    updateServiceAt(idx, { amount: next, ...keepQuantityIfPresent(service, next) });
                  }}
                  aria-label={t("common.increase", "Increase")}
                  disabled={isPaidFull}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="col-span-3">
              <div className="flex items-start">
                <div className="text-[10px] md:text-xs">{service.title}</div>
              </div>
            </div>
            {/* Discount Input */}
            <div className="col-span-2">
              <Input
                type="text"
                inputMode="decimal"
                className="w-full h-8 text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                value={editingDiscount[idx] ?? (service.discount ? String(service.discount) : "0")}
                disabled={isPaidFull}
                onChange={(e) => {
                  setEditingDiscount(prev => ({ ...prev, [idx]: e.target.value }));
                }}
                onBlur={(e) => {
                  handleEditDiscount(idx, e.target.value);
                  setEditingDiscount(prev => {
                    const next = { ...prev };
                    delete next[idx];
                    return next;
                  });
                }}
                onFocus={(e) => {
                  if (editingDiscount[idx] === undefined) {
                    setEditingDiscount(prev => ({ ...prev, [idx]: String(service.discount ?? 0) }));
                  }
                  e.target.select();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                aria-label={t("appointment.payment.discount_in_percent", "Discount in %")}
              />
            </div>
            {/* Сумма, BYN (editable) */}
            <div className="col-span-3 text-center">
              <div className="flex items-center gap-1 justify-center">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => {
                    const current = getServiceLineTotal(service);
                    const next = Math.max(0, current - 0.50);
                    handleEditLineTotal(idx, next.toString());
                    setEditingLineTotal(prev => ({ ...prev, [idx]: next.toFixed(2) }));
                  }}
                  aria-label={t("appointment.payment.decrease_amount", "Decrease amount")}
                  disabled={isPaidFull}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <Input
                  type="text"
                  inputMode="decimal"
                  className="w-16 h-8 text-right [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  value={editingLineTotal[idx] ?? getServiceLineTotal(service).toFixed(2)}
                  disabled={isPaidFull}
                  onChange={(e) => {
                    // Store the raw input value during editing
                    setEditingLineTotal(prev => ({ ...prev, [idx]: e.target.value }));
                  }}
                  onBlur={(e) => {
                    // On blur, apply the changes and clear editing state
                    handleEditLineTotal(idx, e.target.value);
                    // No need to clear manual cost here, handleEditLineTotal manages discount
                    setEditingLineTotal(prev => {
                      const next = { ...prev };
                      delete next[idx];
                      return next;
                    });
                  }}
                  onFocus={(e) => {
                    // On focus, initialize editing state if needed
                    if (editingLineTotal[idx] === undefined) {
                      setEditingLineTotal(prev => ({ ...prev, [idx]: getServiceLineTotal(service).toFixed(2) }));
                    }
                    // Select all text for easier editing
                    e.target.select();
                  }}
                  onKeyDown={(e) => {
                    // Apply on Enter key
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onWheel={(e) => (e.currentTarget as HTMLElement).blur()}
                  aria-label={t("appointment.payment.amount_in_byn", "Amount")}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => {
                    const current = getServiceLineTotal(service);
                    const next = current + 0.50;
                    handleEditLineTotal(idx, next.toString());
                    setEditingLineTotal(prev => ({ ...prev, [idx]: next.toFixed(2) }));
                  }}
                  aria-label={t("appointment.payment.increase_amount", "Increase amount")}
                  disabled={isPaidFull}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="col-span-1 text-right text-[10px] md:text-xs">
              {/* Display manual_cost if paid_full and differs from first_cost */}
              {(() => {
                if (packageCovered.has(Number(service.id))) {
                  return <span className="text-rose-600">{t("appointment.payment.by_package", "package")}</span>;
                }
                if (isPaidFull) {
                  const m = Number(service.manual_cost);
                  const f = Number(service.first_cost);
                  // Check if manual_cost is present and different from first_cost
                  if (service.manual_cost != null && !isNaN(m) && !isNaN(f) && Math.abs(m - f) > 0.01) {
                    return formatCurrency(m);
                  }
                }
                return formatCurrency(service.cost_to_pay ?? service.price);
              })()}
            </div>
            <div className="col-span-1 flex justify-end">
              <button
                type="button"
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500"
                onClick={() => handleRemoveService(service.id)}
                disabled={isPaidFull || (appointment?.services?.length ?? 0) <= 1}
                title={
                  (appointment?.services?.length ?? 0) <= 1
                    ? t("appointment.modal.last_service_required", "An appointment must keep at least one service")
                    : undefined
                }
                aria-label={t("common.delete", "Delete")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <PackagePaySelect serviceId={Number(service.id)} />
          </div>
        ))}

        <div className="grid grid-cols-12 gap-2 py-4 border-b items-center font-medium">
          <div className="col-span-2"></div>
          <div className="col-span-3">{t("appointment.payment.total", "Total")}</div>
          <div className="col-span-2"></div>
          <div className="col-span-3 text-right"></div>
          <div className="col-span-1 text-right">{payment?.totalAmountString ?? "—"}</div>
          <div className="col-span-1"></div>
        </div>
        {/* client packages: sell one now, then pay today's service with it */}
        <div className="pt-2 flex justify-end">
          <SellPackageButton preselect={!isPaidFull} />
        </div>
      </div>

      {/* Payment content */}
      <div>
        {isPaidFull ? (
          <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <div className="text-3xl font-bold text-green-600 mb-2">{t("appointment.payment.paid", "Paid")}</div>
            <div className="text-sm text-gray-500">{t("appointment.payment.order_fully_paid", "Order fully paid")}</div>
            {/* Payment Summary Breakdown */}
            {backendAppointment?.paymentSection?.paymentSummary && (
              <div className="mt-4 w-full max-w-sm">
                <div className="text-xs text-gray-500 mb-2 text-center">{t("appointment.payment.payment_breakdown", "Payment breakdown")}</div>
                <div className="space-y-1">
                  {backendAppointment.paymentSection.paymentSummary.map((item: any) => (
                    <div key={item.accountId} className="flex justify-between items-center text-sm px-4">
                      <span className="flex items-center gap-2">
                        {item.isCash ? (
                          <Banknote className="w-4 h-4 text-blue-600" />
                        ) : (
                          <CreditCard className="w-4 h-4 text-green-600" />
                        )}
                        <span>{item.accountTitle}</span>
                      </span>
                      <span className="font-medium">{formatCurrency(item.totalPaid)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">{t("appointment.payment.to_register", "To register")}</h3>
            </div>

            <div className="flex border-b mb-4 text-sm">
              <button
                type="button"
                className={clsx(
                  "px-4 py-2 -mb-px border-b-2 transition",
                  paymentMode === PaymentMode.QUICK
                    ? "border-primary font-medium"
                    : "border-transparent text-gray-500"
                )}
                onClick={() => {
                  // Switching tabs should not auto-apply a payment; wait for explicit card click
                  setPaymentMode(PaymentMode.QUICK);
                }}
              >
                {t("appointment.payment.quick_payment", "Quick payment")}
              </button>
              <button
                type="button"
                className={clsx(
                  "px-4 py-2 -mb-px border-b-2 transition",
                  paymentMode === PaymentMode.SPLIT
                    ? "border-primary font-medium"
                    : "border-transparent text-gray-500"
                )}
                onClick={() => {
                  setPaymentMode(PaymentMode.SPLIT);
                }}
              >
                {t("appointment.payment.split_payment", "Split payment")}
              </button>
            </div>

            {paymentMethods && paymentMethods.length > 0 ? (
              <>
                {paymentMode === PaymentMode.QUICK && (
                  <div className="grid grid-cols-2 gap-4">
                    {paymentMethods.map((method: any, index: number) => {
                      const accountId = method?.account?.id;
                      const key = accountId ?? index;
                      const isSelected = selectedAccountId !== null && selectedAccountId === accountId;

                      return (
                        <div
                          key={key}
                          className={clsx(
                            "border rounded-md p-4 relative transition cursor-pointer",
                            isSelected
                              ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                              : "border-gray-300",
                            !isSelected && selectedAccountId !== null && selectedAccountId !== accountId && "opacity-50"
                          )}
                          onClick={() => applyQuickSelection(accountId ?? null)}
                        >
                          <div className="flex items-center text-sm mb-1">
                            {method.account?.isCash ? (
                              <Banknote className="w-4 h-4 mr-2 text-blue-600" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2 text-green-600" />
                            )}
                            <span className="font-medium">
                              {method.account?.isCash ? t("appointment.payment.cash", "Cash") : t("appointment.payment.card", "Bank card")}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{method.account?.title || "—"}</div>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            {isSelected ? (
                              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {paymentMode === PaymentMode.SPLIT && (
                  <div className="grid grid-cols-2 gap-4">
                    {paymentMethods.map((method: any, index: number) => {
                      const accountId = method?.account?.id;
                      const key = accountId ?? index;
                      const amountToShow = accountId != null
                        ? splitEditState[accountId] !== undefined
                          ? splitEditState[accountId]
                          : (splitAmounts[accountId] ?? 0).toFixed(2)
                        : "";

                      return (
                        <div
                          key={key}
                          className="border border-gray-300 rounded-md p-4 flex flex-col gap-2"
                        >
                          <div className="flex items-center text-sm">
                            {method.account?.isCash ? (
                              <Banknote className="w-4 h-4 mr-2 text-blue-600" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2 text-green-600" />
                            )}
                            <div>
                              <div className="font-medium">
                                {method.account?.isCash ? t("appointment.payment.cash", "Cash") : t("appointment.payment.card", "Bank card")}
                              </div>
                              <div className="text-xs text-gray-500">{method.account?.title || "—"}</div>
                            </div>
                          </div>
                          <div className="mt-1">
                            <label className="block text-xs text-gray-500 mb-1">{t("appointment.payment.amount_byn", "Amount")}</label>
                            <Input
                              type="text"
                              min={0}
                              step="1"
                              inputMode="decimal"
                              value={amountToShow}
                              disabled={accountId == null}
                              onChange={(e) =>
                                accountId != null &&
                                handleSplitAmountChange(accountId, e.target.value)
                              }
                              onFocus={() => accountId != null && handleSplitInputFocus(accountId)}
                              onBlur={(e) => accountId != null && handleSplitInputBlur(accountId, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.currentTarget.blur();
                                }
                              }}
                              className="text-right"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-amber-200 bg-amber-50/40 px-4 text-center">
                <AlertTriangle className="mb-4 h-10 w-10 text-amber-400" strokeWidth={1.5} />
                <p className="mb-2 text-base text-gray-700">
                  {t("appointment.payment.no_payment_accounts", "No payment accounts available")}
                </p>
                <p className="mb-3 max-w-md text-sm text-gray-500">
                  {t(
                    "appointment.payment.no_payment_accounts_hint",
                    "Create payment accounts first to write off the amount."
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => void goToDashboard("/dashboard/payment-accounts")}
                  className="text-amber-600 hover:text-amber-700 text-sm font-medium underline underline-offset-2"
                >
                  {t("appointment.payment.create_payment_account", "Create payment account")}
                </button>
              </div>
            )}

            {isSplitMode && !isSplitValid && (
              <p className="mt-2 text-xs text-red-500">
                {t("appointment.payment.split_mismatch", "Payment method amounts must match total")} ({payment?.totalAmountString ?? totalAmount}).
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

